/**
 * Leadler pieces shared by the scan dashboard (leads-dashboard.tsx, v0.1.79) and the Huni tab:
 * the firm card (GET /leads/{id}: fit split, the pilot's notes, evidence rows, stage history),
 * the cell card (GET /leads/cells/{cellId}: counters, runs, executed queries), the region panel
 * (progress, pace, cost and yield), the filter tables and the funnel, which lists every stage
 * with its count (growth.leads). Company-level data only.
 */

import { type ReactNode, useState } from 'react';
import {
  CELL_CLASS_LABELS,
  CELL_STATUS_LABELS,
  CELL_STATUSES,
  type CellDetail,
  type CellStatus,
  type FitParts,
  LEAD_STAGE_LABELS,
  type LeadDetail,
  type LeadMapCell,
  type LeadStage,
  type LeadsView,
  PROVIDER_LABELS,
  QUERY_FAMILY_LABELS,
  QUERY_STATUS_LABELS,
  RUN_STATUS_LABELS,
  SCAN_MODE_LABELS,
  STOP_REASON_LABELS,
  type WorldRegion,
} from '../api/leads.js';
import { usePanel } from '../app/context.js';
import { formatAge, formatClock, formatDayMonth, formatNumber, formatRelativeDay, formatUsd } from '../lib/format.js';
import { SectionHeader } from './primitives.js';

/** The saturation rule of the scan: a cell counts as covered from this many queries on. */
export const MIN_QUERIES_PER_CELL = 15;
export const LIST_PAGE = 25;

const FIT_PARTS: { key: keyof Omit<FitParts, 'total'>; label: string; cap: number }[] = [
  { key: 'vehicle', label: 'Araç uyumu', cap: 40 },
  { key: 'market', label: 'Pazar', cap: 20 },
  { key: 'design', label: 'Tasarım', cap: 20 },
  { key: 'size', label: 'Büyüklük', cap: 10 },
  { key: 'gap', label: 'Boşluk', cap: 10 },
];

export const STAGE_FILTERS: { label: string; stages: readonly LeadStage[] | null }[] = [
  { label: 'Tümü', stages: null },
  { label: 'Nitelikli', stages: ['nitelikli', 'dogrulandi', 'temas', 'cevap', 'musteri'] },
  { label: 'Bulundu', stages: ['bulundu'] },
  { label: 'Uygun değil', stages: ['uygun_degil'] },
];

export const SOURCE_LABELS: Readonly<Record<string, string>> = {
  trade_show: 'fuar',
  apollo: 'Apollo',
  osm: 'OSM',
  places: 'Places',
  here: 'HERE',
  gemini: 'Genesis Eye',
  web_search: 'web araması',
  directory: 'dizin',
  inbound: 'gelen',
  referral: 'tavsiye',
  manual: 'elle',
};

const EVIDENCE_KIND_LABELS: Readonly<Record<string, string>> = {
  website: 'web sitesi',
  search_result: 'arama sonucu',
  directory: 'dizin',
  osm: 'OSM',
  map: 'harita',
  registry: 'sicil',
  news: 'haber',
  job_ad: 'iş ilanı',
  trade_show: 'fuar',
  dealer_locator: 'bayi haritası',
  social: 'sosyal',
  vies: 'VIES',
  sercan: 'Sercan',
};

const LOCATION_LABELS: Readonly<Record<string, string>> = {
  own_site: 'firmanın kendi sitesi',
  directory: 'dizin kaydı',
  osm: 'OSM kaydı',
  none: 'yok',
};

const UNFIT_LABELS: Readonly<Record<string, string>> = {
  competitor: 'rakip',
  not_target_type: 'hedef tür değil',
  closed: 'kapalı',
  country_blocked: 'ülke kapalı',
  no_unsolicited_notice: 'istenmeyen ileti kabul etmiyor',
  suppressed: 'durdurma listesinde',
  duplicate: 'kopya',
  no_channel: 'kanal yok',
  sercan_decision: 'Sercan kararı',
  other: 'diğer',
};

export function byPlanOrder(
  a: { wave: number; priority: number; code: string },
  b: { wave: number; priority: number; code: string },
): number {
  return a.wave - b.wave || a.priority - b.priority || a.code.localeCompare(b.code);
}

export function countBy(cells: LeadMapCell[], status: CellStatus): number {
  return cells.filter((cell) => cell.status === status).length;
}

export function sum(cells: LeadMapCell[], key: 'sites' | 'leads' | 'qualifiedLeads' | 'queriesRun'): number {
  return cells.reduce((total, cell) => total + cell[key], 0);
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <dl className="lr-field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </dl>
  );
}

function Chips({ items }: { items: string[] }) {
  return (
    <span className="lr-chips">
      {items.map((item) => (
        <span key={item}>{item}</span>
      ))}
    </span>
  );
}

export function FirmCard({
  detail,
  loading,
  error,
  now,
}: {
  detail: LeadDetail | null;
  loading: boolean;
  error: string | null;
  now: number;
}) {
  if (error !== null) {
    return (
      <p className="placeholder-text" role="status">
        Firma kartı alınamadı: {error}
      </p>
    );
  }
  if (detail === null) {
    return (
      <p className="placeholder-text" role="status">
        {loading ? 'Firma kartı yükleniyor…' : 'Haritadan bir iğneye veya listeden bir firmaya dokun.'}
      </p>
    );
  }
  const s = detail.summary;
  const makes = s?.makes ?? detail.relevanceReason;
  return (
    <article className="lr-card" aria-busy={loading}>
      <header className="lr-card__head">
        <div>
          <h3 className="lr-card__name">{detail.name}</h3>
          <p className="lr-card__where">
            {detail.locality ?? 'konum yok'}
            {detail.cellId === null ? null : (
              <>
                {' '}
                · hücre <span className="mono">{detail.cellId}</span>
              </>
            )}
            {detail.website === null ? null : (
              <>
                {' '}
                ·{' '}
                <a href={detail.website} target="_blank" rel="noopener noreferrer">
                  {detail.domain ?? 'web sitesi'}
                </a>
              </>
            )}
          </p>
        </div>
        <span className={`lr-badge lr-badge--${detail.stage}`}>{LEAD_STAGE_LABELS[detail.stage]}</span>
      </header>
      <div className="lr-fit">
        <div className="lr-fit__total">
          <span className="lr-fit__num mono">{detail.fit.total}</span>
          <span className="ink-2">/ 100 uyum</span>
        </div>
        <dl className="lr-fit__parts">
          {FIT_PARTS.map((part) => (
            <div key={part.key}>
              <dt>{part.label}</dt>
              <dd>
                <span className="lr-fit__track" aria-hidden="true">
                  <span className="lr-fit__bar" style={{ width: `${(detail.fit[part.key] / part.cap) * 100}%` }} />
                </span>
                <span className="mono">
                  {detail.fit[part.key]}/{part.cap}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
      {makes === null ? null : <Field label="Ne yapıyor">{makes}</Field>}
      {s?.ourMatch ? <Field label="Eşleşen ürünümüz">{s.ourMatch}</Field> : null}
      {s && s.vehicles.length > 0 ? (
        <Field label="Araçlar">
          <Chips items={s.vehicles} />
        </Field>
      ) : null}
      {s && s.productCategories.length > 0 ? (
        <Field label="Ürün kategorileri">
          <Chips items={s.productCategories} />
        </Field>
      ) : null}
      <dl className="lr-pairs lr-pairs--card">
        {s?.ownManufacturing ? (
          <div>
            <dt>Kendi üretimi</dt>
            <dd>
              {s.ownManufacturing === 'yes' ? 'Evet' : s.ownManufacturing === 'no' ? 'Hayır' : s.ownManufacturing}
            </dd>
          </div>
        ) : null}
        {s?.tier ? (
          <div>
            <dt>Sınıf</dt>
            <dd>{s.tier}</dd>
          </div>
        ) : null}
        {s?.locationConfidence ? (
          <div>
            <dt>Konum güveni</dt>
            <dd>{LOCATION_LABELS[s.locationConfidence] ?? s.locationConfidence}</dd>
          </div>
        ) : null}
        <div>
          <dt>Kaynak</dt>
          <dd>
            {SOURCE_LABELS[detail.source] ?? detail.source}
            {detail.sourceRef === null ? null : (
              <>
                {' '}
                · <span className="mono">{detail.sourceRef}</span>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Aşama</dt>
          <dd>
            {LEAD_STAGE_LABELS[detail.stage]} · {formatAge(now - detail.stageChangedAt)} önce
            {detail.unfitReason === null ? '' : ` · ${UNFIT_LABELS[detail.unfitReason] ?? detail.unfitReason}`}
          </dd>
        </div>
        {s?.stageReason ? (
          <div>
            <dt>Aşama gerekçesi</dt>
            <dd>{s.stageReason}</dd>
          </div>
        ) : null}
      </dl>
      <h4 className="lr-card__sub">
        Kanıt <span className="mono">{detail.evidence.length}</span>
      </h4>
      {detail.evidence.length === 0 ? (
        <p className="placeholder-text">Bu firma için kaynak satırı yok.</p>
      ) : (
        <ul className="lr-evidence">
          {detail.evidence.map((item) => {
            const phrase = item.extracted.matched_phrase;
            const query = item.extracted.query;
            // A homepage snapshot (v0.1.29): its title, or why it could not be read.
            const title = item.extracted.title;
            const bot = item.extracted.botSignature;
            const parked = item.extracted.parkedSignature;
            const snapshotNote =
              typeof bot === 'string'
                ? `okunamadı, bot duvarı: ${bot}`
                : typeof parked === 'string'
                  ? `park sayfası (${parked})`
                  : typeof title === 'string'
                    ? title
                    : null;
            return (
              <li key={item.evidenceId}>
                <span className="lr-evidence__kind">{EVIDENCE_KIND_LABELS[item.kind] ?? item.kind}</span>
                {item.url === null ? null : (
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="lr-evidence__link">
                    {item.domain ?? item.url}
                  </a>
                )}
                {typeof phrase === 'string' ? (
                  <span className="lr-evidence__phrase">“{phrase}”</span>
                ) : typeof query === 'string' ? (
                  <span className="lr-evidence__phrase">sorgu: {query}</span>
                ) : snapshotNote !== null ? (
                  <span className="lr-evidence__phrase">{snapshotNote}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {s && s.pagesRead.length > 0 ? <p className="ink-2 lr-card__pages">Okunan sayfa: {s.pagesRead.length}</p> : null}
      {detail.history.length > 0 ? (
        <p className="ink-2 lr-card__history">
          Aşama geçmişi:{' '}
          {detail.history
            .map(
              (change) =>
                `${change.fromStage === null ? '' : `${LEAD_STAGE_LABELS[change.fromStage]} → `}${LEAD_STAGE_LABELS[change.toStage]} (${formatRelativeDay(change.changedAt, now)}, ${change.actor})`,
            )
            .join('; ')}
        </p>
      ) : null}
    </article>
  );
}

export type FitFilter = 'Tümü' | '60+' | '75+' | '85+';
export const FIT_FILTERS: { label: FitFilter; min: number }[] = [
  { label: 'Tümü', min: 0 },
  { label: '60+', min: 60 },
  { label: '75+', min: 75 },
  { label: '85+', min: 85 },
];
export const DAY_MS = 24 * 60 * 60 * 1000;
const CELL_QUERIES_PAGE = 30;

/** The region's progress, pace, cost and yield, next to the map. */
export function RegionPanel({
  cells,
  region,
  weeklyCellQuota,
  now,
}: {
  cells: LeadMapCell[];
  region: WorldRegion | null;
  weeklyCellQuota: number;
  now: number;
}) {
  const total = cells.length;
  if (total === 0) return null;
  const done = countBy(cells, 'tamamlandi');
  const partial = countBy(cells, 'kismi');
  const scanning = countBy(cells, 'taraniyor');
  const waiting = countBy(cells, 'taranmadi');
  const scannedAt = cells.flatMap((cell) => (cell.lastScannedAt === null ? [] : [cell.lastScannedAt]));
  const last7 = scannedAt.filter((at) => now - at <= 7 * DAY_MS).length;
  const last28 = scannedAt.filter((at) => now - at <= 28 * DAY_MS).length;
  const remaining = waiting + partial;
  const etaWeeks = weeklyCellQuota > 0 ? Math.ceil(remaining / weeklyCellQuota) : null;
  const cost = cells.reduce((acc, cell) => acc + cell.costMicroUsd, 0);
  const scanned = done + partial + scanning;
  const perCell = scanned > 0 ? Math.round(cost / scanned) : null;
  const customers = cells.reduce((acc, cell) => acc + cell.customers, 0);
  const results = cells.reduce((acc, cell) => acc + cell.resultsSeen, 0);
  const label = `${region?.name ?? ''}: ${total} hücrenin ${done} tamamlandı, ${partial} kısmi, ${scanning} taranıyor, ${waiting} sırada`;
  return (
    <div className="rp">
      <div className="rp__bar" role="img" aria-label={label}>
        {CELL_STATUSES.map((status) => {
          const n = countBy(cells, status);
          return n === 0 ? null : (
            <span key={status} className={`rp__seg rp__seg--${status}`} style={{ width: `${(100 * n) / total}%` }} />
          );
        })}
      </div>
      <dl className="lr-pairs rp__pairs">
        <div>
          <dt>İlerleme</dt>
          <dd className="mono">
            {done} / {total} <span className="ink-2">tamam</span>
            {partial > 0 ? ` · ${partial} kısmi` : ''}
            {scanning > 0 ? ` · ${scanning} taranıyor` : ''}
          </dd>
        </div>
        <div>
          <dt>Firma</dt>
          <dd className="mono">
            {formatNumber(sum(cells, 'leads'))} <span className="ink-2">bulundu</span> ·{' '}
            {formatNumber(sum(cells, 'qualifiedLeads'))} <span className="ink-2">nitelikli</span> · {customers}{' '}
            <span className="ink-2">müşteri</span>
          </dd>
        </div>
        <div>
          <dt>Hız</dt>
          <dd className="mono">
            {last7} <span className="ink-2">hücre / 7 gün</span> · {last28} <span className="ink-2">/ 28 gün</span>
          </dd>
        </div>
        <div>
          <dt>Kotayla bitiş</dt>
          <dd className="mono">
            {etaWeeks === null ? 'kota yok' : remaining === 0 ? 'bitti' : `~${etaWeeks} hafta`}{' '}
            <span className="ink-2">
              ({remaining} hücre, haftada {weeklyCellQuota})
            </span>
          </dd>
        </div>
        <div>
          <dt>Maliyet</dt>
          <dd className="mono">
            {formatUsd(cost, 2)}
            {perCell === null ? null : <span className="ink-2"> · hücre başına {formatUsd(perCell, 2)}</span>}
          </dd>
        </div>
        <div>
          <dt>Sorgu · sonuç</dt>
          <dd className="mono">
            {formatNumber(sum(cells, 'queriesRun'))} · {formatNumber(results)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/** The cell card: the engine's counters, then its runs and executed queries from GET /leads/cells/{cellId}. */
export function CellCard({
  cell,
  detail,
  loading,
  error,
  now,
  onClose,
}: {
  cell: LeadMapCell;
  detail: CellDetail | null;
  loading: boolean;
  error: string | null;
  now: number;
  onClose: () => void;
}) {
  const [queryLimit, setQueryLimit] = useState(CELL_QUERIES_PAGE);
  const shown = detail?.cell ?? cell;
  return (
    <article className="cc" aria-labelledby={`cc-${cell.cellPk}`}>
      <div className="cc__head">
        <h3 id={`cc-${cell.cellPk}`} className="cc__title">
          Hücre <span className="mono">{cell.cellId}</span>
        </h3>
        <button type="button" className="btn btn--secondary cc__close" onClick={onClose}>
          Kapat
        </button>
      </div>
      <p className="cc__line">
        {cell.localities.length > 0 ? `${cell.localities.join(', ')} · ` : ''}
        {CELL_STATUS_LABELS[cell.status]} · {CELL_CLASS_LABELS[cell.cellClass]} ·{' '}
        {SCAN_MODE_LABELS[cell.scanMode] ?? cell.scanMode}
        {cell.plannedOrder !== null ? ` · plan sırası ${cell.plannedOrder}` : ''}
      </p>
      <dl className="lr-pairs">
        <div>
          <dt>Sorgu</dt>
          <dd className="mono">{formatNumber(shown.queriesRun)}</dd>
        </div>
        <div>
          <dt>Sonuç</dt>
          <dd className="mono">{formatNumber(shown.resultsSeen)}</dd>
        </div>
        <div>
          <dt>Çözümlenen firma</dt>
          <dd className="mono">{formatNumber(shown.companiesResolved)}</dd>
        </div>
        <div>
          <dt>Yeni nitelikli</dt>
          <dd className="mono">{shown.newQualified}</dd>
        </div>
        <div>
          <dt>Doygunluk</dt>
          <dd className="mono">
            {shown.queriesSinceLastNew} / {shown.satK} <span className="ink-2">yeni firma görmeden sorgu</span>
          </dd>
        </div>
        <div>
          <dt>Maliyet</dt>
          <dd className="mono">{formatUsd(shown.costMicroUsd, 2)}</dd>
        </div>
        <div>
          <dt>Son tarama</dt>
          <dd className="mono">{shown.lastScannedAt === null ? 'yok' : formatRelativeDay(shown.lastScannedAt, now)}</dd>
        </div>
        <div>
          <dt>Sonraki tarama</dt>
          <dd className="mono">{shown.nextScanAt === null ? 'yok' : formatRelativeDay(shown.nextScanAt, now)}</dd>
        </div>
        <div>
          <dt>Firma</dt>
          <dd className="mono">
            {shown.leads} <span className="ink-2">bulundu</span> · {shown.qualifiedLeads}{' '}
            <span className="ink-2">nitelikli</span> · {shown.customers} <span className="ink-2">müşteri</span>
          </dd>
        </div>
      </dl>
      {loading ? (
        <p className="lr-note" role="status">
          Koşular yükleniyor…
        </p>
      ) : null}
      {error !== null ? (
        <p className="lr-note lr-note--warning" role="status">
          Hücre kartı yüklenemedi: {error}
        </p>
      ) : null}
      {detail !== null ? (
        <>
          <h4 className="cc__h4">
            Koşular <span className="mono">{detail.runs.length}</span>
          </h4>
          {detail.runs.length === 0 ? (
            <p className="placeholder-text">Bu hücrede koşu yok.</p>
          ) : (
            // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable by keyboard.
            <section className="lr-table-wrap" aria-label="Hücre koşuları tablosu" tabIndex={0}>
              <table className="lr-table">
                <thead>
                  <tr>
                    <th scope="col">Başlangıç</th>
                    <th scope="col">Durum</th>
                    <th scope="col" className="num">
                      Sorgu
                    </th>
                    <th scope="col" className="num">
                      Sonuç
                    </th>
                    <th scope="col" className="num">
                      Yeni firma
                    </th>
                    <th scope="col" className="num">
                      Nitelikli
                    </th>
                    <th scope="col">Durma</th>
                    <th scope="col" className="num">
                      Maliyet
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detail.runs.map((run) => (
                    <tr key={run.scanRunId}>
                      <th scope="row" className="mono">
                        {formatDayMonth(run.startedAt)} {formatClock(run.startedAt)}
                      </th>
                      <td>{RUN_STATUS_LABELS[run.status] ?? run.status}</td>
                      <td className="num mono">
                        {run.processed} / {run.plannedQueries}
                      </td>
                      <td className="num mono">{formatNumber(run.resultsSeen)}</td>
                      <td className="num mono">{run.newCompanies}</td>
                      <td className="num mono">{run.newQualified}</td>
                      <td>{run.stopReason === null ? '' : (STOP_REASON_LABELS[run.stopReason] ?? run.stopReason)}</td>
                      <td className="num mono">{formatUsd(run.costMicroUsd, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          <h4 className="cc__h4">
            Sorgular <span className="mono">{detail.queries.length}</span>
          </h4>
          {detail.queries.length === 0 ? (
            <p className="placeholder-text">Bu hücrede sorgu koşmadı.</p>
          ) : (
            // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable by keyboard.
            <section className="lr-table-wrap" aria-label="Hücre sorguları tablosu" tabIndex={0}>
              <table className="lr-table">
                <thead>
                  <tr>
                    <th scope="col">Sorgu</th>
                    <th scope="col">Aile</th>
                    <th scope="col">Sağlayıcı</th>
                    <th scope="col">Durum</th>
                    <th scope="col" className="num">
                      Sonuç
                    </th>
                    <th scope="col" className="num">
                      Yeni aday
                    </th>
                    <th scope="col" className="num">
                      Maliyet
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detail.queries.slice(0, queryLimit).map((query) => (
                    <tr key={query.queryRunId}>
                      <th scope="row" className="cc__query">
                        {query.queryText}
                      </th>
                      <td>{QUERY_FAMILY_LABELS[query.family] ?? query.family}</td>
                      <td>{PROVIDER_LABELS[query.provider] ?? query.provider}</td>
                      <td>
                        {QUERY_STATUS_LABELS[query.status] ?? query.status}
                        {query.errorCode ? ` (${query.errorCode})` : ''}
                      </td>
                      <td className="num mono">{query.resultCount}</td>
                      <td className="num mono">{query.newCandidates}</td>
                      <td className="num mono">{formatUsd(query.costMicroUsd, 3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          {detail.queries.length > queryLimit ? (
            <button type="button" className="lr-more" onClick={() => setQueryLimit(queryLimit + CELL_QUERIES_PAGE)}>
              Daha fazla sorgu ({detail.queries.length - queryLimit})
            </button>
          ) : null}
        </>
      ) : null}
    </article>
  );
}

export function LeadFunnel({ view }: { view: LeadsView }) {
  const { now } = usePanel();
  const flow = view.funnel.filter((stage) => stage.stage !== 'uygun_degil');
  const unfit = view.funnel.find((stage) => stage.stage === 'uygun_degil');
  const max = Math.max(1, ...flow.map((stage) => stage.leads));
  const total = view.funnel.reduce((sumLeads, stage) => sumLeads + stage.leads, 0);
  return (
    <section className="lr-block lr-block--wide" aria-labelledby="lr-huni">
      <SectionHeader id="lr-huni" title="Lead hunisi" count={total} />
      <ol className="lr-funnel" aria-label="Aşamalar">
        {flow.map((stage) => (
          <li key={stage.stage} className="lr-funnel__row">
            <span className="lr-funnel__name">{LEAD_STAGE_LABELS[stage.stage]}</span>
            <span className="lr-funnel__track" aria-hidden="true">
              <span className="lr-funnel__bar" style={{ width: `${(stage.leads / max) * 100}%` }} />
            </span>
            <span className="lr-funnel__count mono">{formatNumber(stage.leads)}</span>
            <span className="lr-funnel__meta">
              {stage.avgFitScore === null ? 'uyum yok' : `uyum ort. ${formatNumber(stage.avgFitScore, 1)}`}
              {stage.avgConfidence === null ? '' : ` · güven ${formatNumber(stage.avgConfidence, 1)}`}
              {stage.oldestInStageSince === null ? '' : ` · en eskisi ${formatAge(now - stage.oldestInStageSince)}`}
              {stage.manualContactOnly > 0 ? ` · ${stage.manualContactOnly} sadece elle temas` : ''}
            </span>
          </li>
        ))}
      </ol>
      {unfit ? (
        <p className="lr-funnel__unfit">
          {LEAD_STAGE_LABELS.uygun_degil}: <span className="mono">{formatNumber(unfit.leads)}</span>
          {unfit.oldestInStageSince === null ? '' : ` · en eskisi ${formatAge(now - unfit.oldestInStageSince)}`}
        </p>
      ) : null}
    </section>
  );
}
